import { useState, useEffect } from "react";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import {
  BookOpen, ArrowLeft, ArrowRight, RotateCcw, CheckCircle,
  XCircle, Brain, ChevronLeft, Zap, Target, Trophy
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Flashcards = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [cards, setCards] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("overview"); // overview | study | results

  useEffect(() => { loadSubjects(); }, []);

  const loadSubjects = async () => {
    try {
      const res = await axios.get(`${API}/flashcards/subjects`);
      setSubjects(res.data);
    } catch { toast.error("Fehler"); }
    finally { setLoading(false); }
  };

  const startStudy = async (subject) => {
    setSelectedSubject(subject);
    setLoading(true);
    try {
      const [cardsRes, progRes] = await Promise.all([
        axios.get(`${API}/flashcards/${subject.id}`),
        axios.get(`${API}/flashcards/progress/${subject.id}`)
      ]);
      setCards(cardsRes.data);
      setProgress(progRes.data);
      setCurrentIndex(0);
      setIsFlipped(false);
      setMode("study");
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const handleAnswer = async (correct) => {
    if (!cards[currentIndex]) return;
    try {
      const res = await axios.post(`${API}/flashcards/progress/${selectedSubject.id}`, null, {
        params: { card_id: cards[currentIndex].id, correct }
      });
      setProgress(res.data);
    } catch {}

    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else {
      setMode("results");
    }
  };

  const resetProgress = async () => {
    if (!selectedSubject) return;
    try {
      await axios.post(`${API}/flashcards/progress/${selectedSubject.id}/reset`);
      toast.success("Fortschritt zurückgesetzt");
      setProgress({ studied: [], correct: [], incorrect: [], total_studied: 0, score: 0 });
      setCurrentIndex(0);
      setIsFlipped(false);
      setMode("study");
    } catch { toast.error("Fehler"); }
  };

  const backToOverview = () => {
    setMode("overview");
    setSelectedSubject(null);
    setCards([]);
    loadSubjects();
  };

  const studiedCount = progress?.studied?.length || 0;
  const correctCount = progress?.correct?.length || 0;
  const totalCards = cards.length;

  if (loading) {
    return (
      <ParticipantLayout title="Lernkarten">
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </ParticipantLayout>
    );
  }

  return (
    <ParticipantLayout title="Lernkarten">
      <div className="space-y-6" data-testid="flashcards-page">
        {/* Overview */}
        {mode === "overview" && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <Brain className="h-6 w-6 text-primary" />
              <div>
                <h2 className="text-lg font-semibold">Industriemeister Lernkarten</h2>
                <p className="text-sm text-muted-foreground">Wähle ein Fach zum Lernen</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subjects.map(s => (
                <Card
                  key={s.id}
                  className="bg-card border-border hover:border-primary/50 cursor-pointer transition-all"
                  onClick={() => startStudy(s)}
                  data-testid={`subject-card-${s.id}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-primary/10 text-primary text-sm font-bold">{s.name}</Badge>
                      <Badge variant="outline">{s.card_count || 0} Karten</Badge>
                    </div>
                    <h3 className="font-semibold">{s.fullName}</h3>
                    <p className="text-sm text-muted-foreground">{s.description}</p>
                    <Button variant="outline" className="w-full mt-2" data-testid={`start-study-${s.id}`}>
                      <BookOpen className="h-4 w-4 mr-2" /> Lernen starten
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Study Mode */}
        {mode === "study" && selectedSubject && cards.length > 0 && (
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={backToOverview} data-testid="back-to-overview">
                <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
              </Button>
              <div className="text-center">
                <p className="font-semibold">{selectedSubject.name} – {selectedSubject.fullName}</p>
                <p className="text-sm text-muted-foreground">Karte {currentIndex + 1} von {totalCards}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={resetProgress} data-testid="reset-progress">
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>

            {/* Progress Bar */}
            <Progress value={((currentIndex + 1) / totalCards) * 100} className="h-2" />

            {/* Flashcard */}
            <div
              className="min-h-[300px] cursor-pointer perspective-1000"
              onClick={() => setIsFlipped(!isFlipped)}
              data-testid="flashcard"
            >
              <Card className={`bg-card border-border transition-all duration-300 min-h-[300px] flex items-center justify-center ${isFlipped ? 'border-primary/50' : ''}`}>
                <CardContent className="p-8 text-center w-full">
                  {!isFlipped ? (
                    <div className="space-y-4">
                      <Badge className="bg-primary/10 text-primary">Frage</Badge>
                      <p className="text-lg font-medium" data-testid="card-question">{cards[currentIndex]?.question}</p>
                      <p className="text-xs text-muted-foreground mt-4">Tippe zum Umdrehen</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Badge className="bg-green-500/10 text-green-500">Antwort</Badge>
                      <p className="text-lg" data-testid="card-answer">{cards[currentIndex]?.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Answer Buttons */}
            {isFlipped && (
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  className="flex-1 max-w-[200px] border-red-500/30 text-red-500 hover:bg-red-500/10"
                  onClick={() => handleAnswer(false)}
                  data-testid="answer-incorrect"
                >
                  <XCircle className="h-5 w-5 mr-2" /> Falsch
                </Button>
                <Button
                  className="flex-1 max-w-[200px] bg-green-600 hover:bg-green-700"
                  onClick={() => handleAnswer(true)}
                  data-testid="answer-correct"
                >
                  <CheckCircle className="h-5 w-5 mr-2" /> Richtig
                </Button>
              </div>
            )}

            {/* Stats */}
            <div className="flex justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-green-500" /> {correctCount} richtig</div>
              <div className="flex items-center gap-1"><Target className="h-4 w-4 text-primary" /> {studiedCount} gelernt</div>
              <div className="flex items-center gap-1"><Zap className="h-4 w-4 text-yellow-500" /> {progress?.score || 0}% Score</div>
            </div>
          </div>
        )}

        {/* Results */}
        {mode === "results" && (
          <div className="max-w-lg mx-auto text-center space-y-6">
            <Trophy className="h-16 w-16 mx-auto text-primary" />
            <h2 className="text-2xl font-bold">Geschafft!</h2>
            <p className="text-muted-foreground">{selectedSubject?.fullName}</p>

            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-primary">{totalCards}</p><p className="text-xs text-muted-foreground">Karten</p>
              </CardContent></Card>
              <Card className="bg-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-500">{correctCount}</p><p className="text-xs text-muted-foreground">Richtig</p>
              </CardContent></Card>
              <Card className="bg-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{progress?.score || 0}%</p><p className="text-xs text-muted-foreground">Score</p>
              </CardContent></Card>
            </div>

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={resetProgress} data-testid="restart-study">
                <RotateCcw className="h-4 w-4 mr-2" /> Nochmal lernen
              </Button>
              <Button onClick={backToOverview} data-testid="back-overview-results">
                <BookOpen className="h-4 w-4 mr-2" /> Andere Fächer
              </Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {mode === "study" && cards.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Keine Lernkarten für dieses Fach vorhanden</p>
            <Button variant="outline" onClick={backToOverview} className="mt-4">Zurück</Button>
          </div>
        )}
      </div>
    </ParticipantLayout>
  );
};

export default Flashcards;
